import {
    MeshBuilder, StandardMaterial, Color3, Vector3,
    Animation
} from "@babylonjs/core";

// ══════════════════════════════════════════════════════════════════
//  TYPES DE BONUS
//  Soin    — Tore vert     (soins immédiats)
//  Force   — Octaèdre orange (dégâts ×2)
//  Vitesse — Sphère cyan   (vitesse légèrement accrue)
// ══════════════════════════════════════════════════════════════════

const BONUS_DEFS = {
    Soin: {
        color:      new Color3(0.05, 1.0, 0.25),
        orbitColor: new Color3(0.0,  0.9, 0.2),
        orbitSpeed: 40,
        createMesh(scene, name) {
            return MeshBuilder.CreateTorus(name, { diameter: 0.9, thickness: 0.28, tessellation: 16 }, scene);
        }
    },
    Force: {
        color:      new Color3(1.0, 0.35, 0.0),
        orbitColor: new Color3(1.0, 0.2,  0.0),
        orbitSpeed: 30,
        createMesh(scene, name) {
            return MeshBuilder.CreatePolyhedron(name, { type: 1, size: 0.55 }, scene);
        }
    },
    Vitesse: {
        color:      new Color3(0.0, 0.75, 1.0),
        orbitColor: new Color3(0.0, 0.9,  1.0),
        orbitSpeed: 18,
        createMesh(scene, name) {
            return MeshBuilder.CreateSphere(name, { diameter: 1.0, segments: 8 }, scene);
        }
    },
};

// Ajusté à 16 pour coller aux nouveaux HP max (80 → soin d'environ 20%)
const HEAL_AMOUNT = 16;

export class RoomBonus {

    static spawn(manager, position) {
        const roll = Math.random();
        let powerType;
        if      (roll < 0.30) powerType = "Soin";
        else if (roll < 0.65) powerType = "Force";
        else                  powerType = "Vitesse";

        const def   = BONUS_DEFS[powerType];
        const scene = manager.scene;
        const baseY = position.y + 0.55;

        const mesh = def.createMesh(scene, `bonus_${powerType}_${Date.now()}`);
        mesh.position.copyFrom(position);
        mesh.position.y = baseY;
        mesh.isPickable = false;

        const mat = new StandardMaterial(`bonusMat_${powerType}`, scene);
        mat.diffuseColor  = new Color3(0.04, 0.04, 0.04);
        mat.emissiveColor = def.color;
        mat.specularColor = def.color;
        mat.alpha = 0.95;
        mesh.material = mat;

        // ── Anneau au sol ──
        const groundRing = MeshBuilder.CreateDisc(`bonusRing_${Date.now()}`, { radius: 1.3, tessellation: 32 }, scene);
        groundRing.rotation.x = Math.PI / 2;
        groundRing.position.copyFrom(position);
        groundRing.position.y = 0.03;
        groundRing.isPickable = false;
        const ringMat = new StandardMaterial(`bonusRingMat_${Date.now()}`, scene);
        ringMat.emissiveColor = def.color;
        ringMat.alpha = 0.32;
        ringMat.backFaceCulling = false;
        groundRing.material = ringMat;

        const ringPulseAnim = new Animation("ringPulse", "scaling.x", 30, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        ringPulseAnim.setKeys([{ frame: 0, value: 1.0 }, { frame: 30, value: 1.45 }, { frame: 60, value: 1.0 }]);
        const ringPulseAnimZ = new Animation("ringPulseZ", "scaling.z", 30, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        ringPulseAnimZ.setKeys([{ frame: 0, value: 1.0 }, { frame: 30, value: 1.45 }, { frame: 60, value: 1.0 }]);
        groundRing.animations.push(ringPulseAnim, ringPulseAnimZ);
        scene.beginAnimation(groundRing, 0, 60, true);

        // ── Anneau orbital ──
        const orbitRing = MeshBuilder.CreateTorus(`bonusOrbit_${Date.now()}`, { diameter: 1.5, thickness: 0.07, tessellation: 28 }, scene);
        orbitRing.position.copyFrom(position);
        orbitRing.position.y = baseY + 0.1;
        orbitRing.rotation.z = Math.PI / 2;
        orbitRing.isPickable = false;
        const orbitMat = new StandardMaterial(`bonusOrbitMat_${Date.now()}`, scene);
        orbitMat.emissiveColor = def.orbitColor;
        orbitMat.alpha = 0.85;
        orbitRing.material = orbitMat;

        const orbitAnim = new Animation("orbitSpin", "rotation.y", 30, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        orbitAnim.setKeys([{ frame: 0, value: 0 }, { frame: def.orbitSpeed, value: Math.PI * 2 }]);
        orbitRing.animations.push(orbitAnim);
        scene.beginAnimation(orbitRing, 0, def.orbitSpeed, true);

        // ── Flottement vertical ──
        const floatAnim = new Animation("bonusFloat", "position.y", 30, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        floatAnim.setKeys([
            { frame:  0, value: baseY },
            { frame: 30, value: baseY + 0.38 },
            { frame: 60, value: baseY },
        ]);
        mesh.animations.push(floatAnim);

        // ── Rotation Y ──
        const rotAnim = new Animation("bonusRot", "rotation.y", 30, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        rotAnim.setKeys([{ frame: 0, value: 0 }, { frame: 60, value: Math.PI * 2 }]);
        mesh.animations.push(rotAnim);

        scene.beginAnimation(mesh, 0, 60, true);

        // Les deux satellites sont stockés dans metadata pour que
        // RoomCleanup.clear() puisse les détruire si le bonus n'est pas ramassé.
        mesh.metadata = { powerType, groundRing, orbitRing };
        manager.bonusCrates.push(mesh);
    }

    static checkInteraction(manager, player) {
        for (let i = manager.bonusCrates.length - 1; i >= 0; i--) {
            const crate = manager.bonusCrates[i];

            // Guard : le crate peut avoir été disposé par RoomCleanup entre deux frames
            if (!crate || crate.isDisposed()) {
                manager.bonusCrates.splice(i, 1);
                continue;
            }

            if (!player.mesh || !crate.intersectsMesh(player.mesh, false)) continue;

            const { powerType, groundRing, orbitRing } = crate.metadata;

            if (powerType === "Soin") {
                player.heal(HEAL_AMOUNT);
            } else {
                player.collectPower(powerType);
            }

            if (manager.audioManager) manager.audioManager.playSound("bonus");

            if (groundRing && !groundRing.isDisposed()) groundRing.dispose();
            if (orbitRing  && !orbitRing.isDisposed())  orbitRing.dispose();
            crate.dispose();
            manager.bonusCrates.splice(i, 1);
        }
    }
}